import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    OnInit,
    TemplateRef,
    ViewEncapsulation,
    booleanAttribute,
    computed,
    input,
    model,
    signal,
} from '@angular/core';

import { DsButtonBase } from '@frontend/ui/shared';

export const DS_BUTTON_KIND_ARRAY = ['primary', 'secondary', 'tertiary', 'success', 'utility'] as const;
export type DsButtonKind = (typeof DS_BUTTON_KIND_ARRAY)[number];

export const DS_BUTTON_VARIANTS_ARRAY = ['filled', 'outline', 'flat', 'flat-reduced'] as const;
export type DsButtonVariant = (typeof DS_BUTTON_VARIANTS_ARRAY)[number];

export const DS_BUTTON_SIZES_ARRAY = ['small', 'medium', 'large'] as const;
export type DsButtonSize = (typeof DS_BUTTON_SIZES_ARRAY)[number];

@Component({
    selector: 'button[ds-button], a[ds-button]',
    exportAs: 'ds-button',
    imports: [CommonModule],
    templateUrl: './button.component.html',
    styleUrl: './button.component.scss',
    host: {
        'class': 'ds-button',
        '[class]': 'hostClass()',
        '[class.ds-btn-disabled]': 'disabled() ? true : null',
        '[class.ds-button-loading]': 'loading() ? true : null',
        '[class.ds-button-truncate]': 'truncate() ? true : null',
        '[attr.aria-disabled]': 'disabled() || loading() ? true : null',
        '[attr.aria-label]': 'loadingAriaLabel()',
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DsButton extends DsButtonBase implements OnInit {
    public readonly variant = model<DsButtonVariant>('filled');
    public readonly size = model<DsButtonSize>('large');
    public readonly kind = model<DsButtonKind>('primary');

    public readonly loading = input(false, { transform: booleanAttribute });
    public readonly truncate = input(false, { transform: booleanAttribute });
    public readonly wrapText = input(false, { transform: booleanAttribute });
    private readonly originalAriaLabel = signal<string | null>(null);

    // TODO: Convert to signal after ngmock is fixed due to sports usage
    // eslint-disable-next-line @angular-eslint/prefer-signals
    @ContentChild('loadingTemplate') protected readonly loadingTemplate?: TemplateRef<any>;

    protected readonly loadingAriaLabel = computed(() => {
        const original = this.originalAriaLabel();

        if (original) {
            return original;
        }
        return this.loading() ? 'Loading' : null;
    });

    protected readonly hostClass = computed(() => {
        const variantKindClass = `ds-btn-${this.variant()}-${this.kind()}`;
        const sizeClass = this.variant() === 'flat-reduced' ? `ds-btn-${this.size()}-reduced` : `ds-btn-${this.size()}`;
        return `${variantKindClass} ${sizeClass}`;
    });

    ngOnInit() {
        const original = this.elementRef.nativeElement.getAttribute('aria-label');
        this.originalAriaLabel.set(original);
    }
}

export const DS_BUTTON_NOT_SUPPORTED_CONFIG: { [key: string]: DsButtonKind[] } = {
    'flat': ['secondary', 'primary', 'success'],
    'outline': ['success', 'utility'],
    'filled': [],
    'flat-reduced': ['primary', 'secondary', 'success'],
};
